import { ApolloClient, ApolloLink, split, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { WebSocketLink } from '@apollo/client/link/ws'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import localforage from 'myStore'

import { customFetchWithToken, fetchWithTimeout, setClientForAuth } from 'services/jwt'

import { checkOffline } from 'popups'

import RestClient from 'services/restClient'
import settingSyncService from 'services/settingSync'
import ouCalendarService from 'services/ouCalendar'
import { settingsMigration } from 'redux/settings/sagas'
import { getSettingKey } from 'redux/settings/sagas'
import type { MigrationAccessor } from 'redux/settings/types'
import { SETTINGS_ALLOWED_KEYS } from 'redux/settings/settingMapping'
import { infoDB } from 'myStore'
import { store as reduxStore } from 'index'

import conf from './conf'

const httpLink = new HttpLink({
  uri: conf.SERVER.URL,
  fetch: customFetchWithToken as any,
})

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await localforage.getItem<string>('app.user.token')
  // return the headers to the context so httpLink can read them

  const newHeaders: Record<string, any> = {
    ...headers,
  }

  if (token != null) {
    newHeaders.Authorization = `JWT ${token}`
  }

  return {
    headers: newHeaders,
  }
})

let link: ApolloLink

if (conf.SERVER.wsURL == null) {
  link = authLink.concat(ApolloLink.from([httpLink]))
} else {
  console.log(' wsURL : ', conf.SERVER.wsURL)

  // websocket - sub protocol = 'graphql-ws' 인 경우 초기화.
  const wsLink = new WebSocketLink(
    new SubscriptionClient(conf.SERVER.wsURL, {
      reconnect: true,
    }),
  )

  link = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    authLink.concat(ApolloLink.from([httpLink])),
  )
}

const cache = new InMemoryCache({
  typePolicies: {
    UserWorkspaceNodeConnection: {
      merge: false,
    },
    WorkspaceAppconfigNodeConnection: {
      merge: false,
    },
  },
})

const client = new ApolloClient({
  link,
  cache,
  queryDeduplication: false,
})

export default client

// Landing client
const httpLinkForLanding = new HttpLink({
  uri: conf.SERVER.URL,
  fetch: customFetchWithToken as any,
})

const authLinkForLanding = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await localforage.getItem<string>('app.user.token')

  // return the headers to the context so httpLink can read them

  const newHeaders: Record<string, any> = {
    ...headers,
  }

  if (token != null) {
    newHeaders.Authorization = `JWT ${token}`
  }

  return {
    headers: newHeaders,
  }
})

const linkForClient = authLinkForLanding.concat(ApolloLink.from([httpLinkForLanding]))

const cacheForLanding = new InMemoryCache()
export const clientForLanding = new ApolloClient({
  link: linkForClient,
  cache: cacheForLanding,
  queryDeduplication: false,
})

// Refresh token client
const httpLinkForRefreshToken = new HttpLink({
  uri: conf.SERVER.URL,
  // 기본 fetch는 타임아웃이 없어 토큰 갱신 요청이 hang되면 영원히 settle되지 않는다.
  // 재발급 로직이 없는 순수 타임아웃 fetch를 주입해 무한 대기를 방지한다.
  fetch: fetchWithTimeout as any,
})

const authLinkForRefreshToken = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await localforage.getItem<string>('app.user.refreshToken')
  // return the headers to the context so httpLink can read them

  const newHeaders: Record<string, any> = {
    ...headers,
  }

  if (token != null) {
    newHeaders.Authorization = `JWT ${token}`
  }

  return {
    headers: newHeaders,
  }
})

const linkForRefreshToken = authLinkForRefreshToken.concat(
  ApolloLink.from([httpLinkForRefreshToken]),
)

const cacheForRefreshToken = new InMemoryCache()

export const clientForRefreshToken = new ApolloClient({
  link: linkForRefreshToken,
  cache: cacheForRefreshToken,
  queryDeduplication: false,
})

// RestClient instances
export const authClient = new RestClient(conf.SERVER.authURL, fetch, {
  onCallError: () => {
    checkOffline()
  },
})

setClientForAuth(clientForRefreshToken, authClient)

export const authClientWithToken = new RestClient(
  conf.SERVER.authURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const restLandingClient = new RestClient(conf.SERVER.restLandingURL, fetch, {
  onCallError: () => {
    checkOffline()
  },
})

export const restClientNoToken = new RestClient(conf.SERVER.restLandingURL, fetch, null)

export const logoClient = new RestClient(conf.SERVER.logoApiURL, fetch, null)

export const orgChartClient = new RestClient(
  conf.SERVER.orgChartURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const restClient = new RestClient(
  conf.SERVER.restURL,
  customFetchWithToken as any,
  'app.user.token',
)

settingSyncService.init(restClient)
ouCalendarService.init(restClient)

// 서버 데이터 수신 시 검증/마이그레이션/초기화 콜백 등록
const propertyAccessor: MigrationAccessor<Record<string, unknown>> = {
  type: 'server',
  getValue: (target, key) => target[key],
  setValue: (target, key, value) => {
    target[key] = value
  },
  removeValue: (target, key) => {
    delete target[key]
  },
}

settingSyncService.setSettingMigrator((setting) => settingsMigration(setting, propertyAccessor))

settingSyncService.setSettingsProvider(() => {
  const state = reduxStore.getState() as Record<string, unknown>
  const settings = state.settings as Record<string, unknown>
  const syncSettings: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(settings)) {
    if (SETTINGS_ALLOWED_KEYS.has(key)) {
      syncSettings[key] = value
    }
  }
  return syncSettings
})

settingSyncService.setETagAccessor({
  load: async () => {
    const value = await infoDB.getItem(getSettingKey('eTag'))
    return value as string | null
  },
  save: async (value: string | null) => {
    if (value) {
      await infoDB.setItem(getSettingKey('eTag'), value)
    } else {
      await infoDB.removeItem(getSettingKey('eTag'))
    }
  },
})

export const contactClient = new RestClient(
  conf.SERVER.contactURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const mailboxApiClient = new RestClient(
  conf.SERVER.mailboxApiURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const scheduleApiClient = new RestClient(
  conf.SERVER.scheduleApiURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const secureSendApiClient = new RestClient(
  conf.SERVER.secureSendApiURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const readReceiptApiClient = new RestClient(
  conf.SERVER.readReceiptApiURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const aiApiClient = new RestClient(
  conf.SERVER.aiApiURL,
  customFetchWithToken as any,
  'app.user.token',
)

export const storageApiClient = new RestClient(
  conf.SERVER.storageApiURL,
  customFetchWithToken as any,
  'app.user.token',
)

// OpenSearch 이메일 검색 API (OFM-212).
// RestClient 기본 토큰 접두사는 `JWT`이지만 OpenSearch API는 `Bearer`를 요구하므로
// getAuthFunc로 `Authorization: Bearer <app.user.token>`를 명시적으로 주입한다.
export const openSearchApiClient = new RestClient(
  conf.SERVER.openSearchApiURL,
  customFetchWithToken as any,
  'app.user.token',
  async (token: string) => ({ key: 'Authorization', value: `Bearer ${token}` }),
)

export const officeNextClient = new RestClient(
  conf.SERVER.officeNextURL,
  window.fetch,
  'app.user.providerToken',
  async (token: string) => {
    return Promise.resolve({
      key: 'Authorization',
      value: `Bearer ${token}`,
    })
  },
)

export const restClient0 = new RestClient('', customFetchWithToken as any, 'app.user.token')
